import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import RecruiterExplorerGate from './RecruiterExplorerGate';

interface FeatureBlockModalProps {
  featureName: string;
  requiredUserType: 'explorer' | 'recruiter';
  currentUserType: 'explorer' | 'recruiter';
  onClose?: () => void;
}

const FeatureBlockModal: React.FC<FeatureBlockModalProps> = ({
  featureName,
  requiredUserType,
  currentUserType,
  onClose
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');

  // Recruiters on explorer-only features get the hiring pitch + demo CTA,
  // not a dismissible bottom sheet that sent them to "Hiring Board".
  if (requiredUserType === 'explorer' && currentUserType === 'recruiter') {
    return <RecruiterExplorerGate />;
  }

  const getMessage = () => {
    if (requiredUserType === 'explorer') {
      return {
        title: i18n.language === 'de' ? 'Explorer Feature' : 'Explorer Feature',
        description: i18n.language === 'de' 
          ? `${featureName} ist nur für Explorer:innen verfügbar. Als Recruiter:in hast du Zugang zum AI-native ATS mit Candidate Search, Tracking, Fit-Analyse und Reach Outs.`
          : `${featureName} is only available for Explorers. As a Recruiter, you have access to the AI-native ATS with Candidate Search, Tracking, Fit Analysis, and Reach Outs.`,
        cta: i18n.language === 'de' ? 'Zum Hiring Board' : 'Go to Hiring Board',
        ctaLink: '/?tab=hire',
      };
    } else {
      return {
        title: i18n.language === 'de' ? 'Möchtest du gefunden werden?' : 'Want to get discovered?',
        description: i18n.language === 'de'
          ? 'Teile deinen CV, zeige Interesse an Unternehmen und werde von Recruiter:innen kontaktiert! Lade deinen CV hoch, um sichtbar zu werden.'
          : 'Share your CV, show interest in companies, and get contacted by recruiters! Upload your CV to get visible.',
        cta: i18n.language === 'de' ? 'CV hochladen' : 'Upload my CV',
        ctaLink: '/?tab=inspire',
      };
    }
  };

  const message = getMessage();
  const isPositiveNudge = requiredUserType === 'recruiter';

  return (
    <div className="fixed inset-0 z-[50000] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm mx-0 sm:mx-4 overflow-hidden">
        <div className={`px-4 sm:px-5 py-3 sm:py-4 ${isPositiveNudge 
          ? 'bg-gradient-to-r from-pink-500 to-pink-600' 
          : 'bg-gradient-to-r from-slate-700 to-slate-800'}`}>
          <div className="flex items-center gap-2.5 text-white">
            {isPositiveNudge ? (
              <Sparkles className="w-5 h-5 flex-shrink-0" />
            ) : (
              <Lock className="w-5 h-5 flex-shrink-0" />
            )}
            <h3 className="text-base sm:text-lg font-bold">{message.title}</h3>
          </div>
        </div>

        <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
          <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed">
            {message.description}
          </p>

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-2.5 pt-1">
            <button
              onClick={() => onClose ? onClose() : navigate(-1)}
              className="px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-xl transition-colors flex items-center justify-center gap-1.5"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {i18n.language === 'de' ? 'Zurück' : 'Back'}
            </button>
            <button
              onClick={() => navigate(message.ctaLink)}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-all hover:shadow-md flex items-center justify-center gap-1.5 ${
                isPositiveNudge
                  ? 'bg-pink-500 hover:bg-pink-600'
                  : 'bg-slate-700 hover:bg-slate-800'
              }`}
            >
              {isPositiveNudge && <Sparkles className="w-3.5 h-3.5" />}
              {message.cta}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureBlockModal;

